import { WASH_KIND_INFO, type WashKind } from '@technohull/shared';

// The Audit Log in words (Administration › Audit Log): what each logged action means ("service.write" is
// "Edited Service"), and what a resource that isn't a sheet row points at ("ticket:7" is "Ticket T-7"). A sheet
// row's boat, work and column come from the worker (db/auditDescribe.ts). The logged key stays in each cell's
// tooltip, and an action the list doesn't know yet reads as its key, tidied ("fuel_tank.set" as "Fuel tank set").

export const AUDIT_ACTION_NAMES: Record<string, string> = {
  'release.published': 'New version launched',
  'auth.login': 'Signed in',
  'auth.login.fail': 'Sign-in failed',
  'auth.sso.google': 'Signed in with Google',
  'auth.link.google': 'Connected a Google account',
  'service.write': 'Edited Service',
  'service.add_row': 'Added a Service row',
  'service.new_visit': 'Added a new Service visit',
  'service.delete_row': 'Deleted a Service row',
  'service.add_column': 'Added a Service column',
  'service.auto_flag': 'Flagged a new Service vessel',
  'service.photo.upload': 'Added a Service photo',
  'service.photo.delete': 'Deleted a Service photo',
  'service.photo.note': 'Edited a Service photo note',
  'servicereq.approved': 'Approved a service request',
  'servicereq.declined': 'Declined a service request',
  'grp.write': 'Edited GRP',
  'grp.add_row': 'Added a GRP row',
  'grp.delete_row': 'Deleted a GRP row',
  'grp.add_boat': 'Added a GRP boat',
  'grp.apply_tasks': 'Applied GRP QA rows',
  'overview.write': 'Edited the Overview',
  'overview.confirm': 'Confirmed a delivery',
  'deck.write': 'Edited Deck',
  'deck.add_row': 'Added a Deck row',
  'deck.delete_row': 'Deleted a Deck row',
  'deck.notify_deck_cap': 'Deck cap fitted',
  'pdi.write': 'Edited PDI',
  'ds.write': 'Edited Programming',
  'production.task.write': 'Ticked an assembly task',
  'production.rename': 'Renamed in Assembly Progress',
  'loading.set': 'Edited Loading',
  'loading.bulk': 'Set all on Loading',
  'loading.push': 'Synced Loading to PDI',
  'loading.sync': 'Synced Loading with PDI',
  'pdi.add_boat': 'Added a boat to PDI',
  'order.confirm': 'Confirmed an order',
  'order.edit': 'Edited an order',
  'order.edit_identity': 'Changed an order’s customer, vessel or SAP',
  'order.edit_session.start': 'Started editing an order',
  'order.edit_session.submit': 'Saved an order edit',
  'order.delete': 'Deleted an order',
  'order.restore': 'Restored an order',
  'order.export': 'Exported an order',
  'order.unlock': 'Unlocked an order',
  'order.relock': 'Locked an order again for Sales',
  'order.open_sales': 'Opened an order for Sales to change',
  'order.accept': 'Accepted an order (Assembly)',
  'order.production_on': 'Put an order in production',
  'order.production_off': 'Took an order out of production',
  'order.lock': 'Locked an order for assembly',
  'sales.order.save': 'Saved a sales order',
  'sales.order.delete': 'Deleted a sales order',
  'ordersheet.set': 'Edited the Order Sheet',
  'ordersheet.import': 'Imported the Order Sheet',
  'ordersheet.delete': 'Deleted from the Order Sheet',
  'ordersheet.restore': 'Restored on the Order Sheet',
  'orderfile.save': 'Saved an order file',
  'bom.upload': 'Uploaded the STD lists',
  'bom.delete': 'Removed a BOM list',
  'bom.link': 'Changed a BOM rule',
  'bom.std': 'Changed standard equipment',
  'assembly.order.set': 'Edited an assembly order',
  'customer.create': 'Added a customer',
  'customer.edit': 'Edited a customer',
  'customer.archive': 'Archived a customer',
  'customer.restore': 'Restored a customer',
  'customer.merge': 'Merged customers',
  'customer.unmerge': 'Undid a customer merge',
  'customer.duplicates.scan': 'Looked for duplicate customers',
  'customer.duplicates.dismiss': 'Dismissed a duplicate customer',
  'employee.create': 'Added an employee',
  'employee.edit': 'Edited an employee',
  'employee.delete': 'Deleted an employee',
  'overtime.add': 'Added to the overtime roster',
  'overtime.remove': 'Removed from the overtime roster',
  'overtime.import': 'Imported the overtime roster',
  'overtime.sort': 'Sorted the overtime roster',
  'files.upload': 'Uploaded a file',
  'files.delete': 'Deleted a file',
  'photo.upload': 'Added a photo',
  'photo.delete': 'Deleted a photo',
  'fueling.delete': 'Deleted a fueling record',
  'hin.import': 'Imported HINs',
  'hin.set': 'Set a HIN',
  'hin.edit': 'Changed a HIN',
  'schedule.import': 'Imported the Schedule',
  'schedule.vessel.upsert': 'Edited the Schedule',
  'schedule.vessel.delete': 'Removed a boat from the Schedule',
  'template.add': 'Added to the assembly template',
  'template.cell': 'Edited the assembly template',
  'template.delete': 'Deleted from the assembly template',
  'template.move': 'Moved in the assembly template',
  'engine_order.set': 'Ticked Engine Orders',
  'wash.set': 'Changed the Washing Plan',
  'ticket.create': 'Sent a ticket',
  'ticket.decide': 'Answered a ticket',
  'ticket.withdraw': 'Withdrew a ticket',
  'ticket.file.add': 'Added a ticket screenshot',
  'ticket.file.remove': 'Removed a ticket screenshot',
  'announcement.delete': 'Deleted an announcement',
  'admin.config.set': 'Changed a setting',
  'admin.role.create': 'Added a role',
  'admin.role.update': 'Edited a role',
  'admin.role.delete': 'Deleted a role',
  'admin.role.permissions': 'Changed a role’s permissions',
  'admin.user.create': 'Added a user',
  'admin.user.delete': 'Deleted a user',
  'admin.user.active': 'Activated or suspended a user',
  'admin.user.name': 'Renamed a user',
  'admin.user.role': 'Changed a user’s role',
  'admin.user.roles': 'Changed a user’s roles',
  'admin.user.departments': 'Changed a user’s departments',
  'admin.user.overrides': 'Changed a user’s permissions',
  'admin.user.password': 'Set a password',
  'admin.user.merge': 'Merged accounts',
  'admin.user.alias.remove': 'Removed a merged address',
};

/** an action in words: its name, else its key tidied ("fuel_tank.set" → "Fuel tank set") */
export function auditActionName(action: string): string {
  const named = AUDIT_ACTION_NAMES[action];
  if (named) return named;
  if (action.startsWith('auth.sso.')) return `Signed in with ${action.slice(9).replace(/^\w/, (c) => c.toUpperCase())}`;
  const words = action.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function detailOf(detail: unknown): Record<string, unknown> {
  if (detail && typeof detail === 'object') return detail as Record<string, unknown>;
  if (typeof detail !== 'string' || !detail) return {};
  try { const o = JSON.parse(detail) as unknown; return o && typeof o === 'object' ? (o as Record<string, unknown>) : {}; } catch { return {}; }
}

/** a resource that isn't a sheet row, in words; null to show it as logged */
export function auditResourceName(resource: string | null, detail: unknown, nameOf: (email: string) => string): string | null {
  if (!resource) return null;
  if (!resource.includes(':')) return resource.includes('@') ? nameOf(resource) : null;
  const [kind, ...rest] = resource.split(':');
  const id = rest.join(':');
  if (!id) return null;
  switch (kind) {
    case 'ticket': return /^\d+$/.test(id) ? `Ticket T-${id}` : null;
    case 'sap': return `SAP ${id}`;
    case 'order': return `Order ${id}`;
    case 'customer': return `Customer #${id}`;
    case 'employee': return `Employee #${id}`;
    case 'announcement': return `Announcement #${id}`;
    case 'request': return `Service request #${id}`;
    case 'season': return `Season ${id}`;
    case 'bom': return `${id} BOM list`;
    case 'name': return id;
    case 'loading': {
      const [sap, item] = id.split('/');
      return item ? `Loading · SAP ${sap} · item ${item}` : `Loading · SAP ${sap}`;
    }
    case 'wash': {
      // wash:<source>:<boat key: the SAP, or a Service row id>:<kind>; the boat's name rides in the detail
      const [source, key, wash] = id.split(':');
      const what = WASH_KIND_INFO[wash as WashKind]?.short ?? wash ?? '';
      const boat = detailOf(detail).boat;
      const who = typeof boat === 'string' && boat.trim() ? boat.trim() : source === 'prod' && key ? `SAP ${key}` : '';
      return [`Wash: ${what}`, who].filter(Boolean).join(' · ');
    }
    default: return null;
  }
}
